import WrapperLoader from 'components/loader/WrapperLoader'
import { SMALL_BUTTON_LOADER_SIZE } from 'constants/loaderConstants'
import PropTypes from 'prop-types'
import React, { useEffect, useState, useMemo, useCallback, memo, useRef } from 'react'
import Accordion from 'react-bootstrap/Accordion'
import Card from 'react-bootstrap/Card'
import Form from 'react-bootstrap/Form'
import InputGroup from 'react-bootstrap/InputGroup'
import FontAwesome from 'react-fontawesome'
import PatrimonyApi from 'services/patrimonyApi'
import { sortByLabel } from 'utils/patrimonyUtils'
import I18n from '../../../resources/i18n/I18n'
import ModalDelete from '../ModalDelete'
import '../configuration.scss'
import './LabelEditionTreePatrimonies.scss'
import ModalEstateCreation from './ModalEstateCreation'
import ModalEstateEdition from './ModalEstateEdition'

const LabelEditionTreePatrimonies = ({
  searchValue,
  onSearchValueChange,
  patrimonies,
  group,
  error,
  createPatrimony,
  createSector,
  createAgency,
  deletePatrimony,
  deleteSector,
  deleteAgency,
  editPatrimony,
  editSector,
  editAgency,
  importPatrimony,
}) => {
  const [showModalEdition, setShowModalEdition] = useState(false)
  const [showModalDelete, setShowModalDelete] = useState(false)
  const [showModalCreation, setShowModalCreation] = useState(false)
  const [itemToCreate, setItemToCreate] = useState(null)
  const [itemToEdit, setItemToEdit] = useState(null)
  const [itemToDelete, setItemToDelete] = useState(null)
  const [titleModal, setTitleModal] = useState(null)
  const [concernedSectors, setConcernedSectors] = useState([])
  const [loadingState, setLoadingState] = useState({
    downloadPatrimony: false,
  })
  const [loadedAgencies, setLoadedAgencies] = useState({})
  const [loadingAgencies, setLoadingAgencies] = useState({})
  const searchTimeoutRef = useRef(null)

  useEffect(() => {
    const concernedSectors = []
    if (patrimonies && searchValue) {
      const regex = new RegExp(`(${searchValue})`, 'gi')

      patrimonies.forEach((patrimony) => {
        patrimony.sectors?.forEach((sector) => {
          const agencies = loadedAgencies[sector.id] || []
          agencies.forEach((agency) => {
            const parts = agency.label.split(regex)
            parts
              .filter((part) => part)
              .forEach((part) => {
                regex.test(part) && concernedSectors.push(`${sector.id}-sector`)
              })
          })
        })
      })
      setConcernedSectors(concernedSectors)
    } else {
      setConcernedSectors([])
    }
  }, [searchValue, patrimonies, loadedAgencies])

  useEffect(() => {
    return () => {
      if (searchTimeoutRef?.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  const toggleModalCreation = () => {
    setShowModalCreation(!showModalCreation)
  }

  const getTitleModal = (type) => {
    switch (type) {
      case 'patrimony':
        return group.labelPatrimony
      case 'sector':
        return group.labelSector
      case 'agency':
        return group.labelAgency
      default:
        return null
    }
  }

  const onCreateLabel = (e, item, itemTypeParent) => {
    if (e) {
      e.stopPropagation()
    }

    if (itemTypeParent === 'agency' && item && item.id) {
      loadAgenciesForSector(item.id)
    }

    setItemToCreate({ idParent: item && item.id ? item.id : null, itemType: itemTypeParent })
    setTitleModal(
      getTitleModal(itemTypeParent) ? `${I18n.t('CreateConfirmTitleWithType')} ${getTitleModal(itemTypeParent)}` : I18n.t('CreateConfirmTitle'),
    )
    toggleModalCreation()
  }

  const onLabelCreation = (label, code) => {
    const newItem = { ...itemToCreate, label: label, code: code }
    switch (itemToCreate.itemType) {
      case 'patrimony':
        return createPatrimony(newItem).then((result) => {
          if (!result || !result.description) {
            setItemToCreate(null)
          }
          return result
        })
      case 'sector':
        return createSector(newItem).then((result) => {
          if (!result || !result.description) {
            setItemToCreate(null)
          }
          return result
        })
      case 'agency':
        return createAgency(newItem).then((result) => {
          if (!result || !result.description) {
            setItemToCreate(null)
            if (result && result.id && newItem.idParent) {
              const newAgency = {
                id: result.id,
                label: newItem.label,
                code: newItem.code,
                idSector: newItem.idParent,
                active: true,
              }
              setLoadedAgencies((prev) => ({
                ...prev,
                [newItem.idParent]: [...(prev[newItem.idParent] || []), newAgency],
              }))
            }
          }
          return result
        })
      default:
        break
    }
  }

  const toggleModalDelete = () => {
    setShowModalDelete(!showModalDelete)
  }

  const onDeleteLabel = (e, item, itemType) => {
    if (e) {
      e.stopPropagation()
    }

    setItemToDelete({ ...item, itemType: itemType })
    setTitleModal(`${I18n.t('DeleteConfirmTitleWithType')} ${getTitleModal(itemType)} "${item.label}"`)
    toggleModalDelete()
  }

  const deleteLabel = () => {
    switch (itemToDelete.itemType) {
      case 'patrimony':
        deletePatrimony(itemToDelete)
        break
      case 'sector':
        deleteSector(itemToDelete)
        break
      case 'agency':
        try {
          const promise = deleteAgency(itemToDelete)
          const sectorId = itemToDelete.idSector || itemToDelete.id_sector
          if (loadedAgencies[sectorId]) {
            setLoadedAgencies((prev) => {
              const filteredAgencies = prev[sectorId].filter((a) => a.id !== itemToDelete.id)
              return {
                ...prev,
                [sectorId]: filteredAgencies,
              }
            })
          }
          if (promise && typeof promise.then === 'function') {
            promise
              .then(() => {})
              .catch((err) => {
                console.error('Error deleting agency:', err)
              })
          }
        } catch (error) {
          console.error('Error deleting agency:', error)
        }
        break
      default:
        break
    }
    setItemToDelete(null)
  }

  const toggleModalEdition = () => {
    setShowModalEdition(!showModalEdition)
  }

  const onEditLabel = (e, item, itemType) => {
    if (e) {
      e.stopPropagation()
    }
    setItemToEdit({ ...item, itemType: itemType })
    setTitleModal(`${I18n.t('EditConfirmTitleWithYpe')} ${getTitleModal(itemType)}`)
    toggleModalEdition()
  }

  const modifyItemCode = (code) => {
    setItemToEdit({ ...itemToEdit, code: code })
  }

  const modifyItemLabel = (label) => {
    setItemToEdit({ ...itemToEdit, label: label })
  }

  const editLabel = () => {
    switch (itemToEdit.itemType) {
      case 'patrimony':
        return editPatrimony(itemToEdit).then((result) => {
          if (!result || !result.description) {
            setItemToEdit(null)
          }
          return result
        })
      case 'sector':
        return editSector(itemToEdit).then((result) => {
          if (!result || !result.description) {
            setItemToEdit(null)
          }
          return result
        })
      case 'agency':
        return editAgency(itemToEdit).then((result) => {
          if (!result || !result.description) {
            setItemToEdit(null)
            if (loadedAgencies[itemToEdit.idSector]) {
              setLoadedAgencies((prev) => ({
                ...prev,
                [itemToEdit.idSector]: prev[itemToEdit.idSector].map((a) =>
                  a.id === itemToEdit.id ? { ...a, label: itemToEdit.label, code: itemToEdit.code } : a,
                ),
              }))
            }
          }
          return result
        })
      default:
        break
    }
  }

  const onActivateAgency = (e, agency) => {
    e.stopPropagation()
    const newAgency = { ...agency, active: !agency.active }
    editAgency(newAgency).then(() => {
      const sectorId = agency.idSector || agency.id_sector
      if (loadedAgencies[sectorId]) {
        setLoadedAgencies((prev) => {
          const updatedAgencies = prev[sectorId].map((a) => (a.id === agency.id ? { ...a, active: !a.active } : a))
          return {
            ...prev,
            [sectorId]: updatedAgencies,
          }
        })
      }
    })
  }

  const AgencyItem = memo(({ agency, searchValue, onButtonClick, onHighlight }) => (
    <div className={`agency__container configuration container ${agency.active ? 'agency__item--active' : 'agency__item--inactive'}`}>
      <span className="category-subtitle">
        {onHighlight(agency.label, searchValue || '')}
        {!agency.active && <i> {I18n.t('Disabled')}</i>}
      </span>
      {onButtonClick(agency, 'agency')}
    </div>
  ))
  AgencyItem.displayName = 'AgencyItem'

  const loadAgenciesForSector = useCallback(
    async (sectorId) => {
      if (loadedAgencies[sectorId] || loadingAgencies[sectorId]) {
        return
      }

      setLoadingAgencies((prev) => ({ ...prev, [sectorId]: true }))

      try {
        const agencies = await PatrimonyApi.getAgenciesBySectorId(sectorId)
        const agenciesWithSectorId = agencies.map((agency) => ({
          ...agency,
          idSector: sectorId,
        }))
        setLoadedAgencies((prev) => ({
          ...prev,
          [sectorId]: agenciesWithSectorId,
        }))
      } catch (error) {
        console.error('Error loading agencies:', error)
      } finally {
        setLoadingAgencies((prev) => ({ ...prev, [sectorId]: false }))
      }
    },
    [loadedAgencies, loadingAgencies],
  )

  const getSectorsForPatrimony = useCallback((patrimony) => {
    return patrimony.sectors || []
  }, [])

  const getAgenciesForSector = useCallback(
    (sector) => {
      return loadedAgencies[sector.id] || []
    },
    [loadedAgencies],
  )

  const sortedPatrimonies = useMemo(() => {
    return patrimonies ? patrimonies.sort(sortByLabel) : []
  }, [patrimonies])

  const renderHighlight = useCallback((text, highlight) => {
    if (!highlight.trim()) {
      return <span>{text}</span>
    }
    const regex = new RegExp(`(${highlight})`, 'gi')
    const parts = text.split(regex)
    return (
      <span>
        {parts
          .filter((part) => part)
          .map((part, i) =>
            regex.test(part) ? (
              <mark className="px-0" key={i}>
                {part}
              </mark>
            ) : (
              <span key={i}>{part}</span>
            ),
          )}
      </span>
    )
  }, [])

  const renderButton = useCallback(
    (item, itemType, itemTypeChild = null) => (
      <div className="d-flex gap-x-2">
        {itemTypeChild ? (
          <div className="action-button small" onClick={(e) => onCreateLabel(e, item, itemTypeChild)}>
            <FontAwesome name="plus" />
          </div>
        ) : (
          <div className="action-button small" onClick={(e) => onActivateAgency(e, item)}>
            <FontAwesome name="ban" />
          </div>
        )}
        <div className="action-button small" onClick={(e) => onDeleteLabel(e, item, itemType)}>
          <FontAwesome name="trash" />
        </div>
        <div className="action-button small" onClick={(e) => onEditLabel(e, item, itemType)}>
          <FontAwesome name="pencil" />
        </div>
      </div>
    ),
    [onCreateLabel, onActivateAgency, onDeleteLabel, onEditLabel],
  )

  const renderAgency = useCallback(
    (sector) => {
      const agencies = getAgenciesForSector(sector)
      if (loadingAgencies[sector.id]) {
        return (
          <Accordion.Collapse key="loading" eventKey={`${sector.id}-sector`}>
            <Card.Body>
              <div className="text-center p-3">
                <FontAwesome name="spinner" spin /> {I18n.t('LoadingAgencies')}
              </div>
            </Card.Body>
          </Accordion.Collapse>
        )
      }

      if (agencies.length === 0) {
        return null
      }

      return (
        <Accordion.Collapse key={`${sector.id}-agencies`} eventKey={`${sector.id}-sector`}>
          <Card.Body>
            {agencies.sort(sortByLabel).map((agency) => (
              <AgencyItem key={agency.id} agency={agency} searchValue={searchValue} onButtonClick={renderButton} onHighlight={renderHighlight} />
            ))}
          </Card.Body>
        </Accordion.Collapse>
      )
    },
    [getAgenciesForSector, loadingAgencies, searchValue, renderButton, renderHighlight],
  )

  const renderSectors = useCallback(
    (patrimony) => {
      const sectors = getSectorsForPatrimony(patrimony)

      if (sectors.length === 0) {
        return null
      }

      return sectors.sort(sortByLabel).map((sector) => {
        const eventKey = `${sector.id}-sector`
        const isOpen = concernedSectors && !!concernedSectors.find((e) => e === eventKey)
        const accordionAttributes = isOpen ? { activeKey: eventKey } : {}

        const hasLoadedAgencies = loadedAgencies[sector.id] && loadedAgencies[sector.id].length > 0
        const isLoading = loadingAgencies[sector.id]
        const shouldRenderAgencies = hasLoadedAgencies || isLoading

        return (
          <Accordion {...accordionAttributes} key={sector.id}>
            <Card.Body className="p-0">
              <Card key={sector.id}>
                <Accordion.Toggle as={Card.Header} eventKey={eventKey} onClick={() => loadAgenciesForSector(sector.id)}>
                  <div className="d-flex justify-content-between align-content-center">
                    {renderHighlight(sector.label, searchValue || '')}
                    {renderButton(sector, 'sector', 'agency')}
                  </div>
                </Accordion.Toggle>
                {shouldRenderAgencies && renderAgency(sector, isOpen)}
              </Card>
            </Card.Body>
          </Accordion>
        )
      })
    },
    [
      getSectorsForPatrimony,
      concernedSectors,
      loadedAgencies,
      loadingAgencies,
      loadAgenciesForSector,
      searchValue,
      renderButton,
      renderAgency,
      renderHighlight,
    ],
  )

  const exportPatrimony = () => {
    setLoadingState({ ...loadingState, downloadPatrimony: true })
    PatrimonyApi.exportPatrimony().finally(() => setLoadingState({ ...loadingState, downloadPatrimony: false }))
  }

  return (
    <div className="label-edition-tree-patrimonies container-fluid">
      <div className="d-flex justify-content-end gap-x-2">
        <Form.Group className="col-6 ">
          <InputGroup>
            <InputGroup.Prepend>
              <InputGroup.Text>
                <FontAwesome name="search" />
              </InputGroup.Text>
            </InputGroup.Prepend>
            <Form.Control placeholder={I18n.t('PatrimonyEditionSearchPlaceholder')} onChange={onSearchValueChange} />
          </InputGroup>
        </Form.Group>
        <div className="action-button small patrimony" onClick={importPatrimony}>
          <span className="icon-csv" />
        </div>
        <WrapperLoader
          loading={loadingState.downloadPatrimony}
          className="overlay-wrapper__button--small"
          loadingElementsSize={SMALL_BUTTON_LOADER_SIZE}
        >
          <div className="action-button small" onClick={exportPatrimony}>
            <FontAwesome name="download" />
          </div>
        </WrapperLoader>
        <div className="action-button small" onClick={(e) => onCreateLabel(e, null, 'patrimony')}>
          <FontAwesome name="plus" />
        </div>
      </div>
      <div className="row">
        <p className="error text-center"> {error} </p>
        {sortedPatrimonies.map((item) => {
          return (
            <Card className="w-100 row mb-3" key={item.id}>
              <Card.Header className="bg-transparent border-0 font-weight-bold primary-text-color">
                <div className="d-flex justify-content-between align-items-center">
                  {renderHighlight(item.label, searchValue || '')}
                  {renderButton(item, 'patrimony', 'sector')}
                </div>
              </Card.Header>
              {renderSectors(item)}
            </Card>
          )
        })}
      </div>
      <ModalEstateCreation showModal={showModalCreation} toggleModal={toggleModalCreation} createLabel={onLabelCreation} title={titleModal} />
      <ModalEstateEdition
        showModal={showModalEdition}
        toggleModal={toggleModalEdition}
        item={itemToEdit}
        editLabel={editLabel}
        title={titleModal}
        modifyItemCode={modifyItemCode}
        modifyItemLabel={modifyItemLabel}
      />
      <ModalDelete showModal={showModalDelete} toggleModal={toggleModalDelete} deleteLabel={deleteLabel} title={titleModal} />
    </div>
  )
}

LabelEditionTreePatrimonies.propTypes = {
  patrimonies: PropTypes.array.isRequired,
  createPatrimony: PropTypes.func.isRequired,
  editPatrimony: PropTypes.func.isRequired,
  deletePatrimony: PropTypes.func.isRequired,
  createSector: PropTypes.func.isRequired,
  editSector: PropTypes.func.isRequired,
  deleteSector: PropTypes.func.isRequired,
  createAgency: PropTypes.func.isRequired,
  editAgency: PropTypes.func.isRequired,
  deleteAgency: PropTypes.func.isRequired,
  importPatrimony: PropTypes.func.isRequired,
  error: PropTypes.string,
  group: PropTypes.object,
  onSearchValueChange: PropTypes.func.isRequired,
  searchValue: PropTypes.string,
}

export default LabelEditionTreePatrimonies
